import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoForm() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    clinic_name: '',
    clinic_type: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.clinic_name || !formData.clinic_type) {
      setErrorMsg('Please fill in all fields.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      await axios.post(`${API}/demo-requests`, formData);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <section id="demo-form" data-testid="demo-form-section" className="py-24 md:py-32 px-4 md:px-8 bg-white">
        <div className="max-w-xl mx-auto text-center">
          <CheckCircle size={48} strokeWidth={1.5} className="text-vf-forest mx-auto mb-6" />
          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-vf-charcoal mb-4">
            Thank you — your request is in.
          </h3>
          <p className="font-body text-base text-vf-charcoal/60">
            Our Rural Health Strategist will contact you within 24 hours.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="demo-form" data-testid="demo-form-section" className="py-24 md:py-32 px-4 md:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Copy */}
            <div className="flex flex-col justify-center">
              <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4">
                Request a Demo
              </p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-vf-charcoal tracking-tight mb-6 leading-tight">
                See VitalsFlow in action.
              </h2>
              <p className="font-body text-base text-vf-charcoal/60 leading-relaxed mb-6">
                Get a personalized walkthrough of how VitalsFlow AI can recover lost revenue and
                eliminate documentation burden at your clinic.
              </p>
              <ul className="space-y-3">
                {['30-minute personalized demo', 'Revenue impact analysis for your clinic', 'No commitment required'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-vf-forest shrink-0" />
                    <span className="font-body text-sm text-vf-charcoal/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <div className="bg-vf-salt border border-vf-sage p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/60 font-semibold mb-2 block">
                    Full Name
                  </label>
                  <input
                    data-testid="demo-input-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Dr. Jane Smith"
                    className="w-full font-body text-sm bg-white border border-vf-sage px-4 py-3 text-vf-charcoal placeholder:text-vf-charcoal/30 focus:outline-none focus:ring-1 focus:ring-vf-forest focus:border-vf-forest transition-colors"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/60 font-semibold mb-2 block">
                    Work Email
                  </label>
                  <input
                    data-testid="demo-input-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jane@ruralclinic.org"
                    className="w-full font-body text-sm bg-white border border-vf-sage px-4 py-3 text-vf-charcoal placeholder:text-vf-charcoal/30 focus:outline-none focus:ring-1 focus:ring-vf-forest focus:border-vf-forest transition-colors"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/60 font-semibold mb-2 block">
                    Clinic Name
                  </label>
                  <input
                    data-testid="demo-input-clinic"
                    type="text"
                    name="clinic_name"
                    value={formData.clinic_name}
                    onChange={handleChange}
                    placeholder="Heartland Family Health Center"
                    className="w-full font-body text-sm bg-white border border-vf-sage px-4 py-3 text-vf-charcoal placeholder:text-vf-charcoal/30 focus:outline-none focus:ring-1 focus:ring-vf-forest focus:border-vf-forest transition-colors"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-[0.15em] text-vf-charcoal/60 font-semibold mb-2 block">
                    Clinic Type
                  </label>
                  <Select
                    value={formData.clinic_type}
                    onValueChange={(val) => setFormData((prev) => ({ ...prev, clinic_type: val }))}
                  >
                    <SelectTrigger
                      data-testid="demo-select-type"
                      className="w-full font-body text-sm bg-white border border-vf-sage px-4 py-3 h-auto text-vf-charcoal focus:ring-1 focus:ring-vf-forest"
                    >
                      <SelectValue placeholder="Select clinic type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="independent">Independent RHC</SelectItem>
                      <SelectItem value="hospital-owned">Hospital-Owned</SelectItem>
                      <SelectItem value="fqhc">FQHC</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {status === 'error' && errorMsg && (
                  <p data-testid="demo-form-error" className="font-body text-sm text-red-600">
                    {errorMsg}
                  </p>
                )}

                <button
                  data-testid="demo-submit-button"
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full font-body text-sm font-bold uppercase tracking-[0.15em] bg-vf-mint text-vf-charcoal px-6 py-4 hover:bg-vf-mint/80 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {status === 'loading' && <Loader2 size={16} className="animate-spin" />}
                  Request Pilot Access
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
